/**
 * SMS journal status helpers + sequential poller (callable listSmsMessages only).
 * Mirrors backend journalState submission/delivery enums — not UI labels.
 */

export const SMS_POLL_INTERVAL_MS = 7000

export const SMS_SUBMISSION_STATUS = {
  processing: 'processing',
  submitted: 'submitted',
  failed: 'failed',
}

export const SMS_DELIVERY_STATUS = {
  pending: 'pending',
  delivered: 'delivered',
  failed: 'failed',
  unknown: 'unknown',
}

/** Submission still in flight with TurboSMS. */
export const INTERMEDIATE_SUBMISSION_STATUSES = [SMS_SUBMISSION_STATUS.processing]

/** Delivery waiting for DLR webhook. */
export const INTERMEDIATE_DELIVERY_STATUSES = [SMS_DELIVERY_STATUS.pending]

export const FINAL_SUBMISSION_STATUSES = [
  SMS_SUBMISSION_STATUS.submitted,
  SMS_SUBMISSION_STATUS.failed,
]

export const FINAL_DELIVERY_STATUSES = [
  SMS_DELIVERY_STATUS.delivered,
  SMS_DELIVERY_STATUS.failed,
]

/**
 * Row needs polling while send is processing, or accepted and waiting for delivery.
 * @param {{ submissionStatus?: string|null, deliveryStatus?: string|null }} item
 */
export const isIntermediateSmsStatus = (item = {}) => {
  if (item.submissionStatus === SMS_SUBMISSION_STATUS.processing) {
    return true
  }
  if (
    item.submissionStatus === SMS_SUBMISSION_STATUS.submitted &&
    item.deliveryStatus === SMS_DELIVERY_STATUS.pending
  ) {
    return true
  }
  return false
}

export const hasIntermediateSmsStatuses = (items = []) =>
  Array.isArray(items) && items.some(isIntermediateSmsStatus)

export const DEFAULT_SMS_LIST_LIMIT = 25

/**
 * Background refresh should cover all currently loaded rows (load-more), not only first page.
 * @param {number} visibleCount
 */
export const resolveSmsPollLimit = (visibleCount) =>
  Math.max(DEFAULT_SMS_LIST_LIMIT, Number(visibleCount) > 0 ? Number(visibleCount) : DEFAULT_SMS_LIST_LIMIT)

/**
 * Sequential poller: setTimeout after previous request finishes (never setInterval).
 * Guards against parallel fetches and stale generations (filters / unmount).
 */
export const createSmsJournalPoller = ({
  intervalMs = SMS_POLL_INTERVAL_MS,
  fetchPage,
  shouldContinue,
  isPageVisible = () => typeof document === 'undefined' || !document.hidden,
  setTimeoutFn = setTimeout,
  clearTimeoutFn = clearTimeout,
} = {}) => {
  if (typeof fetchPage !== 'function') {
    throw new Error('fetchPage is required')
  }
  if (typeof shouldContinue !== 'function') {
    throw new Error('shouldContinue is required')
  }

  let timerId = null
  let inFlight = false
  let generation = 0
  let stopped = true

  const clearTimer = () => {
    if (timerId != null) {
      clearTimeoutFn(timerId)
      timerId = null
    }
  }

  const bumpGeneration = () => {
    generation += 1
    return generation
  }

  const schedule = (delayMs = intervalMs) => {
    clearTimer()
    if (stopped) return
    if (!shouldContinue()) return
    if (!isPageVisible()) return
    const scheduledGeneration = generation
    timerId = setTimeoutFn(() => {
      timerId = null
      if (stopped || scheduledGeneration !== generation) return
      run()
    }, delayMs)
  }

  const run = async ({ immediate = false } = {}) => {
    if (stopped) return { skipped: true, reason: 'stopped' }
    if (inFlight) return { skipped: true, reason: 'in_flight' }
    if (!isPageVisible()) return { skipped: true, reason: 'hidden' }
    if (!shouldContinue()) return { skipped: true, reason: 'no_intermediate' }

    inFlight = true
    const requestGeneration = generation

    try {
      await fetchPage({ generation: requestGeneration, immediate })
    } catch (_error) {
      // Caller owns UI; keep polling chain alive on transient failures.
    } finally {
      inFlight = false
      if (!stopped && requestGeneration === generation && shouldContinue() && isPageVisible()) {
        schedule(intervalMs)
      }
    }

    return { skipped: false, generation: requestGeneration }
  }

  return {
    getIntervalMs: () => intervalMs,
    isInFlight: () => inFlight,
    getGeneration: () => generation,
    isStopped: () => stopped,
    bumpGeneration,
    isCurrentGeneration: (value) => value === generation,
    start: () => {
      stopped = false
      clearTimer()
      if (!shouldContinue()) return
      schedule(intervalMs)
    },
    /** Immediate fetch (e.g. tab visible again), then continue interval chain. */
    refreshNow: () => {
      if (stopped) {
        stopped = false
      }
      clearTimer()
      return run({ immediate: true })
    },
    stop: () => {
      stopped = true
      bumpGeneration()
      clearTimer()
    },
    /** Call when filters/page params change so in-flight results are ignored. */
    invalidate: () => {
      bumpGeneration()
      clearTimer()
    },
    pauseForHidden: () => {
      clearTimer()
    },
    resumeFromVisible: () => {
      if (stopped) return Promise.resolve({ skipped: true, reason: 'stopped' })
      clearTimer()
      if (!shouldContinue()) return Promise.resolve({ skipped: true, reason: 'no_intermediate' })
      return run({ immediate: true })
    },
  }
}
